import { registerErrorField } from "@/modules/error/errorFields.ts";
import { SimpleLocalizedError } from "@/modules/l10n/getTransText.ts";
import { msg } from "@lingui/core/macro";
import type { ApiErrorResponse } from "@warrenio/api-spec/spec.oats.gen";
import { isObject } from "@warrenio/utils/isObject";
import { entries, isString } from "remeda";

// NB: Put exceptions in a separate file so they can be imported without importing the entire API module.
// Good for bundling efficiency and avoiding circular dependencies.

//#region ApiError and subclasses

/** Extra information about an error */
export interface ApiErrorMeta {
    /** HTTP status code */
    status?: number;
    /** Request URL */
    requestUrl: string;
    /** HTTP request method */
    requestMethod?: string;
    /** OpenAPI path */
    schemaPath?: string;
    /** x-warren-correlation-id header */
    correlationId?: string | null;
}

export class ApiConnectionError extends SimpleLocalizedError {
    name = "ApiConnectionError";

    constructor(
        cause: Error,
        public readonly meta: ApiErrorMeta,
    ) {
        super(msg`Failed to connect to server`, { cause });
    }
}

export class ApiContentTypeError extends SimpleLocalizedError {
    name = "ApiContentTypeError";
    public loggedResponse: string;

    constructor(
        responseText: string,
        contentType: string | null,
        public readonly meta: ApiErrorMeta,
    ) {
        const ct = JSON.stringify(contentType);
        super(msg`Invalid response type from server: Received ${ct} instead of JSON.`);
        this.loggedResponse = responseText.slice(0, 1000);
    }
}

export class ApiStormwallError extends SimpleLocalizedError {
    name = "ApiStormwallError";

    constructor(public readonly meta: ApiErrorMeta) {
        super(msg`Invalid response from server. Blocked by Stormwall.`);
    }
}

registerErrorField("loggedResponse", isString);

/** Base class for all actual API errors (with a backend-provided message) */
export abstract class ApiError<T = unknown> extends Error {
    name = "ApiError";

    constructor(
        public readonly error: T,
        public readonly meta: ApiErrorMeta,
        public readonly errorMessage: string,
    ) {
        let message = errorMessage;
        const { status, correlationId } = meta;

        if (correlationId != null) {
            message += ` [${correlationId}]`;
        }
        message += ` (HTTP ${status})`;

        super(message);
    }
}

registerErrorField("meta", isObject);

/** Unknown type of error (eg. HTTP 500 Internal Server error) */
export class ApiUnknownError extends ApiError<unknown> {
    name = "ApiUnknownError";
}

/** 504 Gateway Timeout */
export class ApiTimeoutError extends ApiError<unknown> {
    name = "ApiTimeoutError";
}

export interface DevErrorInfo {
    message: string;
    stack?: unknown;
}

/** Error messages related to mock API failures etc. */
export class ApiDevError extends ApiError<DevErrorInfo> {
    name = "ApiDevError";

    constructor(error: DevErrorInfo, meta: ApiErrorMeta) {
        super(error, meta, error.message);
    }
}

/** "Specific" response from eg. the API mutation endpoints */
export class ApiStandardError extends ApiError<ApiErrorResponse> {
    name = "ApiStandardError";

    constructor(error: ApiErrorResponse, meta: ApiErrorMeta) {
        super(error, meta, errorsToString(error));
    }
}

const defaultErrorField = "Error";

function errorsToString(error: ApiErrorResponse) {
    return entries(error.errors)
        .map(([field, msg]) => {
            const prefix = field !== defaultErrorField ? `${field}: ` : "";
            const value = typeof msg === "string" ? msg : JSON.stringify(msg);
            return `${prefix}${value}`;
        })
        .join("\n");
}

export interface GeneralErrorInfo {
    message: string;
}

/** General API errors, such as authentication or other failures */
export class ApiGeneralError extends ApiError<GeneralErrorInfo> {
    name = "ApiGeneralError";

    constructor(error: GeneralErrorInfo, meta: ApiErrorMeta) {
        super(error, meta, error.message);
    }
}

/** 403 Forbidden */
export class ApiCredentialsError extends ApiGeneralError {
    name = "ApiCredentialsError";
}

//#endregion ApiError and subclasses
